/**
 * Alert processing engine.
 * Listens to BLE alert events AND demo source,
 * stores in IndexedDB, fires browser notifications + Vibration API.
 */

import { bleManager, AlertEvent } from './ble-manager';
import { demoSource } from './demo-source';
import { storeAlert } from './db';
import type { Alert } from '../types/alerts';
import { AlertType, ALERT_LABELS, ALERT_DESCRIPTIONS, ALERT_SEVERITY } from '../types/alerts';

type AlertListener = (alert: Alert) => void;
const listeners = new Set<AlertListener>();
let initialized = false;

/** Subscribe to processed alerts */
export function onAlert(fn: AlertListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

/** Vibration patterns by severity (ms on/off) */
const VIBRATION_PATTERNS: Record<string, number[]> = {
  critical: [100, 50, 100, 50, 200],
  high: [100, 80, 100],
  medium: [80],
  low: [],
};

function vibrateForAlert(alert: Alert): void {
  if (!('vibrate' in navigator)) return;
  const pattern = VIBRATION_PATTERNS[alert.severity];
  if (pattern && pattern.length > 0) {
    navigator.vibrate(pattern);
  }
}

function showNotification(alert: Alert): void {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  if (alert.type === AlertType.NONE) return;

  const severity = ALERT_SEVERITY[alert.type];
  const prefix = severity === 'critical' ? '!!' : severity === 'high' ? '!' : 'i';

  new Notification(`[${prefix}] ${ALERT_LABELS[alert.type]}`, {
    body: ALERT_DESCRIPTIONS[alert.type],
    tag: `sentinel-alert-${alert.type}`,
    silent: false,
  });
}

async function processAlert(alert: Alert): Promise<void> {
  // Persist
  try {
    const id = await storeAlert(alert);
    alert.id = id;
  } catch {
    // Storage failed — alert still fires to UI
  }

  // Browser notification
  showNotification(alert);

  // Mobile haptic
  vibrateForAlert(alert);

  // Notify React subscribers
  listeners.forEach((fn) => fn(alert));
}

/** Initialize — idempotent, safe for StrictMode double-invoke */
export function initAlertEngine(): () => void {
  if (initialized) return () => {};
  initialized = true;

  // BLE alerts
  const bleHandler = (e: Event) => {
    const alert = (e as AlertEvent).alert;
    processAlert(alert);
  };
  bleManager.addEventListener('alert', bleHandler);

  // Demo alerts
  const demoUnsub = demoSource.onAlert((alert) => {
    processAlert(alert);
  });

  requestNotificationPermission();

  return () => {
    bleManager.removeEventListener('alert', bleHandler);
    demoUnsub();
    initialized = false;
  };
}
